import {
  Autocomplete as MuiAutocomplete,
  CircularProgress,
  TextField,
  type AutocompleteInputChangeReason,
  type FilterOptionsState,
} from '@mui/material';
import { cn } from 'cn';
import {
  forwardRef,
  memo,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useId,
} from 'react';

import { isFieldRequired } from '@vestigas/utils';

import { normalizeFieldErrors } from './fieldErrors';
import { FieldShell, getFieldDescriptionIds } from './FieldShell';
import { useFieldContext } from './form-context';

/**
 * An option carrying more than its own text, so a consumer can react to a
 * selection with the full record behind it via `onInputChange`.
 */
export type AutocompleteFieldOption = {
  readonly data?: unknown;
  readonly label?: string;
  readonly value: string;
};

export type AutocompleteOption = AutocompleteFieldOption | string;

export type AutocompleteFilterOptions = (
  options: AutocompleteOption[],
  state: FilterOptionsState<AutocompleteOption>,
) => AutocompleteOption[];

const EMPTY_OPTIONS: readonly AutocompleteOption[] = [];

const FLEX_CLASSES = {
  '1': undefined,
  auto: 'flex-auto',
  initial: 'flex-initial',
  none: 'flex-none',
} as const;

type FlexVariant = keyof typeof FLEX_CLASSES;

function getOptionValue(option: AutocompleteOption): string {
  return typeof option === 'string' ? option : option.value;
}

function getOptionLabel(option: AutocompleteOption): string {
  return typeof option === 'string' ? option : (option.label ?? option.value);
}

type AutocompleteProps = {
  readonly ariaDescribedBy?: string;
  readonly autoComplete?: string;
  readonly autoFocus?: boolean;
  readonly filterOptions?: AutocompleteFilterOptions;
  readonly hasErrors: boolean;
  readonly id: string;
  readonly isDisabled: boolean;
  readonly isLoading: boolean;
  readonly listHeader?: ReactNode;
  readonly name?: string;
  readonly onBlur: () => void;
  readonly onChange: (value: string) => void;
  // Matches MUI's own handler signature, since it is passed straight through
  readonly onInputChange: (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
  readonly options: readonly AutocompleteOption[];
  readonly placeholder?: string;
  readonly role?: string;
  readonly value: string;
};

const MemoizedAutocomplete = memo(
  forwardRef<HTMLInputElement, AutocompleteProps>(
    (
      {
        ariaDescribedBy,
        autoComplete = 'off',
        autoFocus,
        filterOptions,
        hasErrors,
        id,
        isDisabled,
        isLoading,
        listHeader,
        name,
        onBlur,
        onChange,
        onInputChange,
        options,
        placeholder,
        role = 'textbox',
        value,
      },
      ref,
    ) => {
      return (
        <MuiAutocomplete
          className="w-full flex-1 focus:outline-none [&_.MuiOutlinedInput-root]:bg-white"
          disabled={isDisabled}
          groupBy={() => '0'} // Group all options under a single group to render a header
          id={id}
          options={options}
          filterOptions={filterOptions}
          getOptionLabel={getOptionLabel}
          renderGroup={(params) => {
            return (
              <div key={params.key} className="[&>li]:px-2">
                {String(params.key) === '0' && listHeader ? listHeader : null}
                {params.children}
              </div>
            );
          }}
          renderInput={(params) => {
            const inputParams = {
              ...params,
              inputProps: {
                ...params.inputProps,
                'aria-describedby': ariaDescribedBy,
                autoComplete,
                className: cn(params.inputProps.className, {
                  'cursor-not-allowed': isDisabled,
                }),
                name,
                role,
              },
            };

            return (
              <TextField
                {...inputParams}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                disabled={isDisabled}
                error={hasErrors}
                inputRef={ref}
                placeholder={placeholder}
                onBlur={() => {
                  onBlur();
                }}
                onChange={(event) => {
                  onChange(event.target.value);
                }}
                slotProps={{
                  input: {
                    ...inputParams.InputProps,
                    endAdornment: isLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      inputParams.InputProps.endAdornment
                    ),
                  },
                }}
              />
            );
          }}
          size="small"
          slotProps={{ listbox: { className: 'p-0 ' } }}
          value={value}
          disableClearable
          freeSolo
          onChange={(_, newValue) => {
            onChange(newValue === null ? '' : getOptionValue(newValue));
          }}
          onInputChange={onInputChange}
        />
      );
    },
  ),
);

MemoizedAutocomplete.displayName = 'MemoizedAutocomplete';

type P = {
  readonly autoComplete?: string;
  readonly autoFocus?: boolean;
  readonly filterOptions?: AutocompleteFilterOptions;
  readonly flex?: FlexVariant;
  readonly hint?: string;
  readonly id?: string;
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
  readonly label?: string;
  readonly listHeader?: ReactNode;
  readonly name?: string;
  readonly onBlur?: () => void;
  /**
   * Called on every input change. `option` is the matching entry from
   * `options`, so a consumer can read `option.data` on a `selectOption` change.
   */
  readonly onInputChange?: (
    value: string,
    reason: AutocompleteInputChangeReason,
    option?: AutocompleteOption,
  ) => void;
  readonly options?: readonly AutocompleteOption[];
  readonly placeholder?: string;
  readonly role?: string;
} & ComponentStyling;

type AutocompleteFieldApi = ReturnType<typeof useFieldContext<string>>;

/**
 * Bridges the field API and the consumer callbacks to MUI's handler shapes.
 *
 * @param field - The TanStack Form field this control edits.
 * @param onBlur - An additional blur callback of the consumer.
 * @param onInputChange - The consumer's input change callback.
 * @param options - The currently offered options, used to resolve a selection.
 */
function useAutocompleteHandlers({
  field,
  onBlur,
  onInputChange,
  options,
}: {
  field: AutocompleteFieldApi;
  onBlur: P['onBlur'];
  onInputChange: P['onInputChange'];
  options: readonly AutocompleteOption[];
}) {
  const handleChange = useCallback(
    (value: string) => {
      field.handleChange(value);
    },
    [field],
  );

  const handleInputChange = useCallback(
    (
      _event: SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      onInputChange?.(
        value,
        reason,
        // The "value" MUI sends is the visible text, which it gets from the option via `getOptionLabel`,
        // so we need to use getOptionLabel to find the matching option.
        options.find((option) => getOptionLabel(option) === value),
      );
    },
    [onInputChange, options],
  );

  const handleBlur = useCallback(() => {
    field.handleBlur();
    onBlur?.();
  }, [field, onBlur]);

  return { handleBlur, handleChange, handleInputChange };
}

export const AutocompleteField = forwardRef<HTMLInputElement, P>(
  (
    {
      autoComplete = 'off',
      autoFocus,
      className,
      filterOptions,
      flex = '1',
      hint,
      id,
      isDisabled = false,
      isLoading = false,
      label,
      listHeader,
      name,
      onBlur,
      onInputChange,
      options = EMPTY_OPTIONS,
      placeholder,
      role = 'textbox',
      style,
    },
    ref,
  ) => {
    const field = useFieldContext<string>();

    const isRequired = isFieldRequired(
      field.options.form.options.validators,
      field.name,
    );

    const errors = normalizeFieldErrors(field.state.meta.errors);
    const hasErrors = errors.length > 0;

    // Provide an id that can be overridden via props
    const generatedId = useId();
    const fieldId = id ?? generatedId;

    const { handleBlur, handleChange, handleInputChange } =
      useAutocompleteHandlers({ field, onBlur, onInputChange, options });

    return (
      <FieldShell
        className={className}
        errorDisplay="overlay"
        errors={errors}
        hint={hint}
        inputId={fieldId}
        isRequired={isRequired}
        label={label}
      >
        <div
          className={cn(
            'flex items-center gap-x-2 overflow-hidden transition-colors duration-200 ease-out',
            FLEX_CLASSES[flex],
          )}
          style={style}
        >
          <MemoizedAutocomplete
            ref={ref}
            ariaDescribedBy={getFieldDescriptionIds({
              hasErrors,
              hasHint: Boolean(hint),
              inputId: fieldId,
            })}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            filterOptions={filterOptions}
            hasErrors={hasErrors}
            id={fieldId}
            isDisabled={isDisabled}
            isLoading={isLoading}
            listHeader={listHeader}
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            onInputChange={handleInputChange}
            options={options}
            placeholder={placeholder}
            role={role}
            value={field.state.value ?? ''}
          />
        </div>
      </FieldShell>
    );
  },
);

AutocompleteField.displayName = 'AutocompleteField';
